import { type MarkedExtension, marked } from 'marked';

// Prevent version-like strings (e.g. test@1.2.3, pkg@0.2.26-beta) from being
// auto-linked as emails. Only suppress when the part after @ lacks a valid TLD
// (2+ consecutive alpha chars after the last dot).
marked.use({
  tokenizer: {
    url(src) {
      const emailMatch = /^[A-Za-z0-9._+-]+@([^\s<]+)/.exec(src);
      if (emailMatch) {
        const domain = emailMatch[1];
        const lastDot = domain.lastIndexOf('.');
        const tld = lastDot >= 0 ? domain.slice(lastDot + 1) : '';
        if (!/^[a-zA-Z]{2,}$/.test(tld)) {
          return {
            type: 'text',
            raw: src.split(/[\s<]/)[0],
            text: src.split(/[\s<]/)[0],
          } as any;
        }
      }
      return false as any;
    },
  },
});

/**
 * Inline extensions that recognise Jira/Confluence-style color and highlight
 * tokens:
 *
 *   {color:rgb(245, 61, 104)}text{color}   →  <span style="color:...">text</span>
 *   {mark:rgb(242, 133, 74)}text{mark}     →  <mark style="background-color:...">text</mark>
 *
 * Exported so any custom `new Marked()` instance (e.g. those created with a
 * custom renderer) can opt in via `instance.use(colorAndHighlightExtensions)`.
 * Marked v15 instances do NOT inherit configuration from the global singleton,
 * so each `new Marked()` must register these explicitly.
 */
export const colorAndHighlightExtensions: MarkedExtension = {
  extensions: [
    {
      name: 'colorOpen',
      level: 'inline',
      start(src) {
        return src.indexOf('{color:');
      },
      tokenizer(src) {
        const match = /^\{color:([^}]+)\}/.exec(src);

        if (!match) return;

        return {
          type: 'colorOpen',
          raw: match[0],
          color: match[1],
        };
      },
      renderer(token) {
        return `<span style="color:${token.color}">`;
      },
    },
    {
      name: 'colorClose',
      level: 'inline',
      start(src) {
        return src.indexOf('{color}');
      },
      tokenizer(src) {
        const match = /^\{color\}/.exec(src);

        if (!match) return;

        return {
          type: 'colorClose',
          raw: match[0],
        };
      },
      renderer() {
        return '</span>';
      },
    },
    {
      name: 'markOpen',
      level: 'inline',
      start(src) {
        return src.indexOf('{mark:');
      },
      tokenizer(src) {
        const match = /^\{mark:([^}]+)\}/.exec(src);

        if (!match) return;

        return {
          type: 'markOpen',
          raw: match[0],
          color: match[1],
        };
      },
      renderer(token) {
        return `<mark style="background-color:${token.color}">`;
      },
    },
    {
      name: 'markClose',
      level: 'inline',
      start(src) {
        return src.indexOf('{mark}');
      },
      tokenizer(src) {
        const match = /^\{mark\}/.exec(src);

        if (!match) return;

        return {
          type: 'markClose',
          raw: match[0],
        };
      },
      renderer() {
        return '</mark>';
      },
    },
  ],
};

// Register on the global singleton so callers using `marked.parse(...)` /
// `marked.parseInline(...)` keep working without changes.
marked.use(colorAndHighlightExtensions);
